import { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, Plane, Train, Edit2 } from 'lucide-react';
import {
    GOOGLE_MAPS_API_KEY,
    LONDON_BOUNDS_BIAS,
    shortenAddress,
    extractPostcode,
    classifyAddressIcon,
} from '../lib/addressCanonicalization.js';

// -----------------------------------------------------------------------------
// Script loader — uses Google's new importLibrary() async pattern.
// https://developers.google.com/maps/documentation/javascript/load-maps-js-api
//
// The older approach of `<script src="...?libraries=places,geometry" onload>`
// races against Google's internal loader — the main script resolves before
// the sub-libraries (places, geometry) are actually on window. We instead use
// the "inline bootstrap loader" that exposes importLibrary(), which properly
// awaits each sub-library.
// -----------------------------------------------------------------------------

let bootstrapPromise = null;

function bootstrapMaps() {
    if (bootstrapPromise) return bootstrapPromise;
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Window not available'));
    }

    // If importLibrary already exists (hot-reload, or previously bootstrapped), reuse it
    if (window.google?.maps?.importLibrary) {
        bootstrapPromise = Promise.resolve();
        return bootstrapPromise;
    }

    const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY;

    bootstrapPromise = new Promise((resolve, reject) => {
        // Inline bootstrap loader, per Google docs
        // This sets up window.google.maps.importLibrary as an async function
        // that we can await to load specific libraries on demand.
        ((g) => {
            let h, a, k, p = 'The Google Maps JavaScript API',
                c = 'google', l = 'importLibrary',
                q = '__ib__',
                m = document, b = window;
            b = b[c] || (b[c] = {});
            const d = b.maps || (b.maps = {}),
                r = new Set(),
                e = new URLSearchParams(),
                u = () => h || (h = new Promise(async (f, n) => {
                    await (a = m.createElement('script'));
                    e.set('libraries', [...r] + '');
                    for (k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]);
                    e.set('callback', c + '.maps.' + q);
                    a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
                    d[q] = f;
                    a.onerror = () => h = n(Error(p + ' could not load.'));
                    a.nonce = m.querySelector('script[nonce]')?.nonce || '';
                    m.head.append(a);
                }));
            d[l]
                ? console.warn(p + ' only loads once. Ignoring:', g)
                : d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n));
        })({
            key,
            v: 'weekly',
        });

        // Wait until the bootstrap has installed importLibrary
        if (window.google?.maps?.importLibrary) {
            resolve();
        } else {
            // Poll briefly — the bootstrap above is synchronous setup,
            // so importLibrary should be present immediately. Belt-and-braces.
            let tries = 0;
            const check = () => {
                if (window.google?.maps?.importLibrary) return resolve();
                if (++tries > 50) return reject(new Error('Google Maps bootstrap never installed importLibrary'));
                setTimeout(check, 20);
            };
            check();
        }
    });

    return bootstrapPromise;
}

async function loadPlacesLibrary() {
    await bootstrapMaps();
    // Force-load both libraries; places for Autocomplete, geometry for bounds.
    await window.google.maps.importLibrary('places');
    await window.google.maps.importLibrary('geometry');
}

// -----------------------------------------------------------------------------
// Icon picker
// -----------------------------------------------------------------------------

function IconForType({ type, className }) {
    if (type === 'airport') return <Plane className={className} />;
    if (type === 'station') return <Train className={className} />;
    return <MapPin className={className} />;
}

// -----------------------------------------------------------------------------
// AddressInput component
// -----------------------------------------------------------------------------
//
// "Enter manually" toggle: when manualMode=true we render a SEPARATE <input>
// element (different React key) with no ref. Google Places Autocomplete binds
// to a specific DOM node, so unmounting that node is the only reliable way to
// stop the suggestion dropdown. clearInstanceListeners / unbindAll do not
// fully detach the dropdown — they leave the input bound to Google's internal
// pac-container, which keeps querying as you type.
//
// Toggling between modes therefore unmounts one input and mounts the other.
// React handles this cleanly via the `key` prop.
// -----------------------------------------------------------------------------

export default function AddressInput({
    value = '',
    onChange,
    placeholder = 'Search address...',
    disabled = false,
    error = null,
    allowManual = true,
    savedAddresses = [],
}) {
    const inputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const wrapRef = useRef(null);
    const [iconType, setIconType] = useState(() => classifyAddressIcon(value));
    const [manualMode, setManualMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [showSaved, setShowSaved] = useState(false);

    // Hide saved-addresses dropdown when clicking outside the wrapper.
    // Mirrors portal AddressInput pattern.
    useEffect(() => {
        if (!showSaved) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setShowSaved(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showSaved]);

    const hasSaved = savedAddresses && savedAddresses.length > 0;
    const isEmpty = !value || !value.trim();

    const handleFocus = () => {
        if (hasSaved && isEmpty) setShowSaved(true);
    };

    const pickSaved = (addr) => {
        // Saved-address row from /customers/:id (customer_addresses table).
        // Shape includes google_place_id + postcode, so we can emit the
        // full address-meta payload — matches a Google Places selection
        // and keeps CC-zone determination working downstream.
        const lat = addr.latitude != null ? Number(addr.latitude) : null;
        const lng = addr.longitude != null ? Number(addr.longitude) : null;
        setShowSaved(false);
        setIconType(classifyAddressIcon(addr.address));
        if (inputRef.current) inputRef.current.value = addr.address;
        onChange?.({
            address: addr.address,
            postcode: addr.postcode || extractPostcode(addr.address),
            latitude: lat,
            longitude: lng,
            google_place_id: addr.google_place_id || null,
            address_source: 'saved',
        });
    };

    useEffect(() => {
        if (manualMode) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        loadPlacesLibrary()
            .then(() => {
                if (cancelled || !inputRef.current) return;
                if (!window.google?.maps?.places?.Autocomplete) {
                    setLoadError('Google Places library did not initialise');
                    setLoading(false);
                    return;
                }

               const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
                                        types: ['establishment', 'geocode'],
                    fields: ['formatted_address', 'name', 'geometry', 'place_id', 'address_components', 'types'],
                });

                const bounds = new window.google.maps.LatLngBounds(
                    new window.google.maps.LatLng(LONDON_BOUNDS_BIAS.sw.lat, LONDON_BOUNDS_BIAS.sw.lng),
                    new window.google.maps.LatLng(LONDON_BOUNDS_BIAS.ne.lat, LONDON_BOUNDS_BIAS.ne.lng)
                );
                ac.setBounds(bounds);

              ac.addListener('place_changed', async () => {
                    const place = ac.getPlace();
                    if (!place || (!place.formatted_address && !place.name)) return;

                    const { display, iconType: newIconType } = shortenAddress(place);
                    const pcComp = (place.address_components || []).find(c => c.types?.includes('postal_code'));
                    let postcode = (pcComp?.long_name)
                        || extractPostcode(display)
                        || extractPostcode(place.formatted_address);
                    const lat = place.geometry?.location?.lat?.();
                    const lng = place.geometry?.location?.lng?.();

                    // Fallback: establishment Place results (hotels, restaurants) often
                    // lack postal_code in address_components. Reverse-geocode the
                    // coords to recover it. Without this, flat-rate rules that key
                    // off postcode district never fire for venue addresses.
                    if (!postcode && typeof lat === 'number' && typeof lng === 'number' && window.google?.maps?.Geocoder) {
                        try {
                            const geocoder = new window.google.maps.Geocoder();
                            postcode = await new Promise((resolve) => {
                                const t = setTimeout(() => resolve(null), 3000);
                                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                                    clearTimeout(t);
                                    if (status !== 'OK' || !results) return resolve(null);
                                    for (const r of results) {
                                        const pc = (r.address_components || []).find(c => c.types?.includes('postal_code'));
                                        if (pc?.long_name) return resolve(pc.long_name);
                                    }
                                    resolve(null);
                                });
                            });
                        } catch {
                            // Best-effort — leave postcode null if geocoder fails.
                        }
                    }

                    setIconType(newIconType);
                    if (inputRef.current) inputRef.current.value = display;

                    lastEmittedRef.current = display;
                    onChange?.({
                        address: display,
                        postcode: postcode || null,
                        latitude: typeof lat === 'number' ? lat : null,
                        longitude: typeof lng === 'number' ? lng : null,
                        google_place_id: place.place_id || null,
                        address_source: 'google_places',
                    });
                });

                autocompleteRef.current = ac;
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Google Maps load failed:', err);
                setLoadError('Could not load address search. Use manual entry.');
                setLoading(false);
            });

        return () => {
            cancelled = true;
            if (autocompleteRef.current && window.google?.maps?.event) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
            }
            autocompleteRef.current = null;
        };
    }, [manualMode]);

    useEffect(() => {
        if (inputRef.current && inputRef.current.value !== value) {
            inputRef.current.value = value || '';
            setIconType(classifyAddressIcon(value));
        }
    }, [value]);

const lastEmittedRef = useRef(value || '');

    useEffect(() => {
        lastEmittedRef.current = value || '';
    }, [value]);

    const handleInput = useCallback((e) => {
        const raw = e.target.value;
        // Once user types, suppress the saved dropdown so Google's
        // autocomplete has the field to itself.
        if (raw && raw.trim().length > 0) setShowSaved(false);

        if (manualMode) {
            setIconType(classifyAddressIcon(raw));
            lastEmittedRef.current = raw;
            onChange?.({
                address: raw,
                postcode: extractPostcode(raw),
                latitude: null,
                longitude: null,
                google_place_id: null,
                address_source: 'manual',
            });
            return;
        }

        // Google mode below.
        setIconType(classifyAddressIcon(raw));

        // Empty: propagate immediately so the parent can clear its draft.
        if (!raw.trim()) {
            lastEmittedRef.current = '';
            onChange?.({
                address: '',
                postcode: null,
                latitude: null,
                longitude: null,
                google_place_id: null,
                address_source: 'manual',
            });
            return;
        }

        // Non-empty free text (no Google Place picked): emit on blur only,
        // and only when the typed text differs from what we last emitted.
        // Stops blur from overwriting a freshly-picked Place's metadata
        // (lat/lng/place_id) with nulls. Free-text edits like "Tour to
        // Cotswolds" now reach the parent's draft instead of being dropped.
        if (e.type === 'blur' && raw !== lastEmittedRef.current) {
            lastEmittedRef.current = raw;
            onChange?.({
                address: raw,
                postcode: extractPostcode(raw),
                latitude: null,
                longitude: null,
                google_place_id: null,
                address_source: 'manual',
            });
        }
    }, [manualMode, onChange]);

    const errorText = Array.isArray(error) ? error.join(', ') : error;

    const inputBaseClass = `w-full h-10 pl-10 pr-3 text-sm border rounded-md bg-white text-slate-900 placeholder-slate-400
        ${errorText ? 'border-red-500' : 'border-slate-300'}
        focus:outline-none focus:ring-1 focus:ring-slate-900 focus:border-slate-900
        disabled:bg-slate-50 disabled:text-slate-500`;

return (
        <div ref={wrapRef} className="w-full">
            <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10">
                    <IconForType type={iconType} className="w-4 h-4" />
                </div>
                {manualMode ? (
                    // Plain text input — no ref, so the Google useEffect can't
                    // bind to it. Different `key` from the Google variant forces
                    // React to mount a fresh DOM node when toggling, which is
                    // the only reliable way to detach Google's dropdown.
                    <input
                        key="manual"
                        type="text"
                        value={value || ''}
                        onChange={handleInput}
                        onFocus={handleFocus}
                        placeholder={placeholder}
                        disabled={disabled}
                        className={inputBaseClass}
                        autoComplete="off"
                    />
                ) : (
                    <input
                        key="google"
                        ref={inputRef}
                        type="text"
                        defaultValue={value}
                        onChange={handleInput}
                        onBlur={handleInput}
                        onFocus={handleFocus}
                        placeholder={loading ? 'Loading address search...' : placeholder}
                        disabled={disabled || loading}
                        className={inputBaseClass}
                        autoComplete="off"
                    />
                )}

                {/* Saved-addresses dropdown — mirrors portal AddressInput.
                    Shows on focus when input is empty and customer has saved
                    addresses. Type any character → suppresses (Google takes over). */}
                {showSaved && hasSaved && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-300 rounded-md shadow-lg overflow-hidden">
                        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                            Saved addresses
                        </div>
                        <ul className="max-h-64 overflow-y-auto">
                            {savedAddresses.map((addr) => (
                                <li key={addr.id}>
                                    <button
                                        type="button"
                                        onMouseDown={(e) => {
                                            // onMouseDown (not onClick) so this fires
                                            // before the input's blur, preventing the
                                            // outside-click handler from closing the
                                            // dropdown before we register the pick.
                                            e.preventDefault();
                                            pickSaved(addr);
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
                                    >
                                        {addr.label && (
                                            <div className="text-sm text-slate-900 font-medium truncate">
                                                {addr.label}
                                            </div>
                                        )}
                                        <div className={`text-xs truncate ${addr.label ? 'text-slate-500 mt-0.5' : 'text-slate-900'}`}>
                                            {addr.address}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            <div className="flex items-center justify-between mt-1 min-h-[18px]">
                <div className="text-xs text-red-600">{errorText || ''}</div>
                {allowManual && !loadError && (
                    <button
                        type="button"
                        onClick={() => setManualMode((m) => !m)}
                        className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
                    >
                        <Edit2 className="w-3 h-3" />
                        {manualMode ? 'Use address search' : 'Enter manually'}
                    </button>
                )}
            </div>
            {loadError && (
                <div className="text-xs text-amber-600 mt-1">{loadError}</div>
            )}
        </div>
    );
}
